import React, { createContext, useContext, type ReactNode } from "react";
import { type JobResponse, type ErrorResponse } from "@taxassist/api-client";
import { type FileItem } from "../components/jobs/fileList";
import { type ProcessingArtifactsRef } from "../components/jobs/ProcessingArtifacts";
import { type UseOpsJobActionsReturn } from "../hooks/useOpsJobActions";
import { type FileActions } from "../hooks/useFileActions";

type ArtifactType = "binder" | "k1_worksheets" | "supporting_doc" | "other";

interface JobDetailsContextValue {
  // Job data
  job: JobResponse | null;
  isLoading: boolean;
  error: ErrorResponse | null;
  context: "client" | "ops";
  variant: "drawer" | "panel";

  // File data
  uploadedFileItems: FileItem[];
  processingArtifactItems: FileItem[];
  isLoadingProcessingResults: boolean;
  processingResultsError: ErrorResponse | null;

  // Refs
  processingArtifactsRef: React.RefObject<ProcessingArtifactsRef>;

  // Actions
  opsActions: UseOpsJobActionsReturn;
  fileActions: FileActions;

  // Specialized delete handlers (fixes the bug where fileType wasn't being passed)
  onUploadedFileDelete: (file: FileItem) => Promise<void>;
  onProcessingArtifactDelete: (file: FileItem) => Promise<void>;

  // Dialog state and handlers
  onUploadClick: () => void;
  onSubmitSuccess: (job: JobResponse) => void;
  onSubmitError: (error: Error, job: JobResponse) => void;
  onClose: () => void;

  // Processing result upload (ops only)
  onProcessingResultUpload?: (
    file: File,
    artifactType: ArtifactType,
    description?: string,
  ) => Promise<void>;
  getProcessingResultFileUrl: (file: FileItem) => Promise<string>;
  onArtifactUpdate?: () => void;
}

const JobDetailsContext = createContext<JobDetailsContextValue | null>(null);

interface JobDetailsProviderProps {
  value: JobDetailsContextValue;
  children: ReactNode;
}

export function JobDetailsProvider({
  value,
  children,
}: JobDetailsProviderProps) {
  return (
    <JobDetailsContext.Provider value={value}>
      {children}
    </JobDetailsContext.Provider>
  );
}

export function useJobDetailsContext(): JobDetailsContextValue {
  const context = useContext(JobDetailsContext);
  if (!context) {
    throw new Error(
      "useJobDetailsContext must be used within a JobDetailsProvider",
    );
  }
  return context;
}
